import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { ProgressRadar } from '../components/Radar';
import { ExchangeTopics } from '../components/ExchangeTopics';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, BookOpen, TrendingUp, Plus, Pencil, Trash2, MessagesSquare } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function EducatorHome() {
  const { user } = useAuth();
  const [youths, setYouths] = useState(null);
  const [groupRadar, setGroupRadar] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', youth_emails: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const [myCode, setMyCode] = useState(null);
  const [generating, setGenerating] = useState(false);
  const { t } = useI18n();

  const loadGroups = () => api.get('/educator/groups').then((r) => setGroups(r.data.groups)).catch(() => {});

  const loadStats = (gid) => {
    const q = gid === 'all' ? '' : `?group_id=${gid}`;
    Promise.all([api.get(`/educator/youths${q}`), api.get(`/educator/radar${q}`)])
      .then(([y, r]) => { setYouths(y.data.youths); setGroupRadar(r.data.radar); })
      .catch(() => setYouths([]));
  };

  const loadMyCode = () => api.get('/educator/my-code').then((r) => setMyCode(r.data.code)).catch(() => {});

  useEffect(() => { loadGroups(); loadMyCode(); }, []);
  useEffect(() => { setYouths(null); loadStats(selectedGroup); }, [selectedGroup]);

  const openNew = () => { setForm({ name: '', youth_emails: '' }); setEditing('new'); };
  const openEdit = (g) => {
    setForm({ name: g.name, youth_emails: (g.youths || []).map((y) => y.email).join('\n') });
    setEditing(g.group_id);
  };

  const saveGroup = async () => {
    try {
      const payload = { name: form.name, youth_emails: form.youth_emails.split('\n').map((e) => e.trim()).filter(Boolean) };
      const { data } = editing === 'new'
        ? await api.post('/educator/groups', payload)
        : await api.put(`/educator/groups/${editing}`, payload);
      if (data.unresolved?.length) {
        toast.warning(t('educator.unresolved', { n: data.unresolved.length, list: data.unresolved.join(', ') }));
      } else {
        toast.success(t('educator.saved'));
      }
      setEditing(null);
      loadGroups();
      loadStats(selectedGroup);
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const removeGroup = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/educator/groups/${deleteTarget}`);
      toast.success(t('educator.deleted'));
      if (selectedGroup === deleteTarget) setSelectedGroup('all');
      loadGroups();
    } catch { toast.error(t('common.error')); }
    setDeleteTarget(null);
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/educator/my-code');
      setMyCode(data.code);
      toast.success(t('educator.codeCreated'));
    } catch { toast.error(t('common.error')); }
    finally { setGenerating(false); }
  };

  const openDetail = async (y) => {
    try {
      const { data } = await api.get(`/educator/youths/${y.user_id}/radar`);
      setDetail({ ...data, percent: y.percent, chapters_completed: y.chapters_completed, chapters_total: y.chapters_total });
    } catch { toast.error(t('common.error')); }
  };

  const translateRadar = (items) => (items || []).map((r) => ({ ...r, axis: t(`axes.${r.key}`, undefined, r.axis) }));

  const avg = youths && youths.length
    ? Math.round(youths.reduce((acc, y) => acc + y.percent, 0) / youths.length)
    : 0;

  return (
    <div data-testid="educator-dashboard">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('educator.title')}</h1>
        <p className="text-base sm:text-lg text-brume mt-3">{t('educator.subtitle', { name: user?.name })}</p>
      </motion.div>

      <div className="mt-6 bg-nuit-800 border border-nuit-700 rounded-3xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="educator-mycode-card">
        <div className="flex-1">
          <p className="font-bold text-creme text-sm">{t('educator.myCode')}</p>
          <p className="text-xs text-brume mt-1">{t('educator.myCodeHelp')}</p>
        </div>
        {myCode ? (
          <div className="flex items-center gap-3">
            <code className="bg-nuit-900 border border-nuit-600 rounded-xl px-4 py-2 font-bold text-or-300 tracking-wider" data-testid="educator-mycode-value">{myCode.code}</code>
            <span className="text-xs text-brume font-bold whitespace-nowrap" data-testid="educator-mycode-uses">{t('educator.myCodeUses', { n: myCode.uses, max: myCode.max_uses })}</span>
          </div>
        ) : (
          <Button onClick={generateCode} disabled={generating} data-testid="educator-mycode-generate"
            className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">
            {generating ? t('common.sending') : t('educator.generateCode')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-8">
        <div className="flex items-center gap-3">
          <label className="text-sm font-bold text-creme">{t('educator.groupLabel')}</label>
          <Select value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger data-testid="group-select" className="w-64 h-12 rounded-2xl bg-nuit-800 border-nuit-600 text-creme font-bold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
              <SelectItem value="all" data-testid="group-option-all" className="text-creme">{t('educator.allYouths')}</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.group_id} value={g.group_id} data-testid={`group-option-${g.group_id}`} className="text-creme">
                  {g.name} ({(g.youths || []).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} data-testid="group-new-button"
          className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> {t('educator.newGroup')}
        </Button>
        {selectedGroup !== 'all' && groups.find((g) => g.group_id === selectedGroup) && (
          <>
            <Button variant="ghost" onClick={() => openEdit(groups.find((g) => g.group_id === selectedGroup))}
              data-testid="group-edit-button" className="rounded-full text-or-300 hover:bg-nuit-700 font-bold gap-2">
              <Pencil className="w-4 h-4" /> {t('educator.edit')}
            </Button>
            <Button variant="ghost" onClick={() => setDeleteTarget(selectedGroup)}
              data-testid="group-delete-button" className="rounded-full text-rose-400 hover:bg-rose-950 font-bold gap-2">
              <Trash2 className="w-4 h-4" /> {t('educator.delete')}
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
          className="bg-or-400 rounded-3xl p-8 self-start">
          <Users className="w-8 h-8 mb-4 text-nuit-900" strokeWidth={2.5} />
          <p className="font-fredoka font-semibold text-5xl text-nuit-950" data-testid="educator-youth-count">{youths ? youths.length : '…'}</p>
          <p className="font-bold mt-1 text-nuit-900">{t('educator.activeYouths')}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.1 }}
          className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 self-start">
          <TrendingUp className="w-8 h-8 mb-4 text-or-400" strokeWidth={2.5} />
          <p className="font-fredoka font-semibold text-5xl text-creme" data-testid="educator-avg-progress">{youths ? `${avg}%` : '…'}</p>
          <p className="font-bold mt-1 text-brume">{t('educator.avgProgress')}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.15 }}
          className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 md:col-span-1 lg:col-span-2">
          <h2 className="text-lg font-semibold text-creme mb-2">{t('educator.groupRadar')}</h2>
          <ProgressRadar data={translateRadar(groupRadar)} testid="educator-group-radar" />
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.2 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 mt-8">
        <h2 className="text-2xl font-semibold text-creme mb-6 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('educator.perYouth')}
        </h2>
        <div className="space-y-4" data-testid="educator-youths-list">
          {(youths || []).map((y) => (
            <button key={y.user_id} onClick={() => openDetail(y)} data-testid={`educator-youth-${y.user_id}`}
              className="w-full flex items-center gap-4 flex-wrap text-left rounded-2xl px-3 py-2 -mx-3 hover:bg-nuit-700/50 transition-colors cursor-pointer">
              <div className="w-40 shrink-0">
                <p className="font-bold text-creme">{y.name}</p>
                {y.age && <p className="text-xs text-brume font-semibold">{t('parent.years', { age: y.age })}</p>}
              </div>
              <div className="flex-1 min-w-[160px]">
                <Progress value={y.percent} className="h-3 rounded-full bg-nuit-700" />
              </div>
              <span className="text-sm font-bold text-brume w-28 text-right">
                {y.chapters_completed}/{y.chapters_total} ({y.percent}%)
              </span>
            </button>
          ))}
          {youths && youths.length === 0 && (
            <p className="text-brume font-semibold" data-testid="educator-empty">
              {selectedGroup === 'all' ? t('educator.emptyAll') : t('educator.emptyGroup')}
            </p>
          )}
          {!youths && <p className="text-brume">{t('common.loading')}</p>}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.25 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 mt-8" data-testid="educator-exchange-general">
        <h2 className="text-2xl font-semibold text-creme mb-2 flex items-center gap-2">
          <MessagesSquare className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('exchange.general')}
        </h2>
        <p className="text-sm text-brume mb-5">{t('exchange.generalIntro')}</p>
        <ExchangeTopics testid="educator-exchange-general-list" />
      </motion.div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">
              {editing === 'new' ? t('educator.newGroup') : t('educator.editGroup')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('educator.groupName')}</label>
              <Input data-testid="group-name-input" value={form.name} placeholder={t('educator.groupNamePlaceholder')}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume font-bold" />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('educator.groupEmails')}</label>
              <Textarea data-testid="group-emails-input" rows={5} value={form.youth_emails}
                onChange={(e) => setForm({ ...form, youth_emails: e.target.value })}
                placeholder="lea.demo@oncroitquonjoue.fr"
                className="rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume font-mono text-sm" />
              <p className="text-xs text-brume mt-2">{t('educator.groupEmailsNote')}</p>
            </div>
            <Button onClick={saveGroup} data-testid="group-save-button" disabled={!form.name.trim()}
              className="w-full h-12 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold">{t('educator.save')}</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-lg rounded-3xl bg-nuit-800 border-nuit-700" data-testid="youth-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-fredoka text-2xl text-creme">
              {detail?.youth?.name}{detail?.youth?.age ? ` — ${t('parent.years', { age: detail.youth.age })}` : ''}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div>
              <p className="text-sm font-bold text-or-300 mb-2" data-testid="youth-detail-progress">
                {t('educator.detailProgress', { done: detail.chapters_completed, total: detail.chapters_total, percent: detail.overall_percent })}
              </p>
              <ProgressRadar data={translateRadar(detail.radar)} testid="youth-detail-radar" />
              <div className="mt-5">
                <p className="text-sm font-bold text-creme mb-3 flex items-center gap-2">
                  <MessagesSquare className="w-4 h-4 text-or-400" strokeWidth={2.5} /> {t('exchange.sectionTitle')}
                </p>
                <ExchangeTopics youthId={detail.youth.user_id} testid="educator-exchange-detail" />
              </div>
              <p className="text-xs text-brume mt-4">{t('educator.diaryPrivate')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl bg-nuit-800 border-nuit-700" data-testid="group-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-fredoka text-2xl text-creme">{t('educator.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-brume">
              {t('educator.deleteText')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="group-delete-cancel"
              className="rounded-full bg-transparent border-2 border-nuit-600 text-brume font-bold hover:bg-nuit-700 hover:text-creme">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={removeGroup} data-testid="group-delete-confirm"
              className="rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold">
              {t('educator.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
